<template>
    <div class="w-100">
        <!-- Page title -->
        <div class="row align-items-center mt-1 mb-3">
            <div class="col-9">
                <h2 class="page-title">
                    {{ pageTitle }}
                </h2>
            </div>
            <!-- Progress bar -->
            <div v-if="progressBar == true" class="col-3 ms-auto d-print-none d-flex align-items-center" id="progressBar">
                <span class="small me-3 w-50 text-end">{{ percentageComplete }}% Complete</span>
                <b-progress class="w-50" :value="currentStep" :max="maxStep" animated></b-progress>
            </div>
        </div>

        <!-- Alert box -->
        <div v-if="this.v$.step[this.currentStep - 1].$error" class="alert alert-danger alert-dismissible" role="alert">
            <div class="d-flex">
                <div>
                    <!-- Download SVG icon from http://tabler-icons.io/i/alert-circle -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon alert-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                        <path d="M12 8v4"></path>
                        <path d="M12 16h.01"></path>
                    </svg>
                </div>
                <div>
                    <span v-if="v$.$errors.length > 1"> Sorry, there are ({{ this.v$.step[this.currentStep - 1].$errors.length }}) errors found please correct the errors to proceed.</span>
                    <span v-if="v$.$errors.length <= 1"> Sorry, there's ({{ this.v$.step[this.currentStep - 1].$errors.length }}) error found please correct the error to proceed.</span>
                </div>
            </div>
            <a class="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
        </div>

        <form id="" action="" class="add-wound add-record" enctype="multipart/form-data">
            <div class="card" v-if="currentStep == 1" title="Wound Photo">
                <div class="card-header">
                    <h3 class="card-title">Upload a photo</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Photo</div>
                                <input
                                    type="file"
                                    class="form-control"
                                    name="photo"
                                    placeholder="Upload an photo"
                                    :class="{
                                        'is-invalid': v$.step[0].photo.$invalid,
                                    }"
                                    @change="onFileChange"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].photo.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <div class="form-label mt-4" v-if="step[0].photo != null">Preview</div>
                                <div class="d-inline-block h-50 w-50">
                                    <img :src="step[0].photo" class="img-light" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a :href="'/care-plans/' + carePlanObject.id ">Cancel</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickNext" class="ml-5 btn btn-primary" id="submit-btn">Save</a>
                    </p>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";

export default {
    name: "carePlanEditPhotoComponent",
    props: ["progressBar", "pageTitle", "userId", "carePlan"], // vue can read kebab props as camel cased vars

    setup() {
        return {v$: useVuelidate()};
    },
    data() {
        var carePlanObject = JSON.parse(this.carePlan);
        var endpointToUse = location.protocol + "//" + window.location.host + "/api/care-plans/" + carePlanObject.id + "/update-photo/" + process.env.MIX_API_KEY;

        return {
            currentStep: 1,
            maxStep: 1,
            percentageComplete: 0,
            errors: 0,
            endpoint: endpointToUse,
            step: [
                // Step 1
                {
                    photo: carePlanObject["photo"] ?? null,
                },
            ],
            carePlanObject: carePlanObject,
        };
    },
    validations() {
        return {
            step: [
                //Step 1
                {
                    photo: {
                        required,
                        $lazy: true,
                        $autoDirty: true,
                    },
                },
            ],
        };
    },

    computed: {},

    methods: {
        onClickNext: function () {
            // Validate fields on current step
            this.v$.step[this.currentStep - 1].$touch();

            // if there are no errors in the current step
            if (!this.v$.step[this.currentStep - 1].$error) {
                if (this.currentStep < this.maxStep) {
                    // Proceed to next step
                    this.currentStep++;
                } else if (this.currentStep == this.maxStep) {
                    // Initialize alerts and toast notifications for displaying message
                    const toastError = Swal.mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: true,
                    });
                    const alertSuccess = Swal.mixin({
                        position: "center",
                        showConfirmButton: true,
                        timer: 3000,
                        timerProgressBar: true,
                    });

                    // Show button spinner
                    document.getElementById("submit-btn").innerHTML = '<i class="fa fa-circle-notch fa-spin"></i>';
                    document.getElementById("submit-btn").disabled = true;

                    // Loop through all fields
                    const formData = {};
                    for (let i = 0; i < this.maxStep; i++) {
                        for (const [key, value] of Object.entries(this.step[i])) {
                            formData[key] = value;
                        }
                    }

                    // Add userID in formData
                    formData["userID"] = this.userId;

                    // Update
                    this.updateCarePlan(formData, toastError, alertSuccess, this.carePlanObject.id);
                }
                this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
            }
        },

        updateCarePlan: function (formData, toastError, alertSuccess, carePlanId) {
            // Call the update endpoint
            axios
                .put(this.endpoint, formData)
                .then((response) => {
                    if (response.data.error == 403) {
                        console.log("Bad Request");
                        console.log(response.data);

                        // Show bad request toast message. This error occurs when the input fields are not in the expected format
                        toastError.fire({
                            type: "warning",
                            title: "The input fields are malformed and cant be processed.",
                        });
                    } else {
                        if (response.data.id == null) {
                            console.log("Validation Error");
                            console.log(response.data);

                            // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the care plan object
                            toastError.fire({
                                type: "warning",
                                title: "There are validation errors found on the form.",
                            });
                        } else {
                            console.log("Success");
                            console.log(response.data);

                            // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the care plan object was updated
                            this.formSuccess = true;
                            alertSuccess
                                .fire({
                                    title: "Success!",
                                    text: "The wound photo has been updated.",
                                    icon: "success",
                                })
                                .then(function () {
                                    console.log(carePlanId);
                                    window.location = "/care-plans/" + carePlanId ;
                                });
                        }
                    }
                })
                .catch((err) => {
                    console.log("Endpoint Error!");
                    console.log(err.response);
                    toastError.fire({type: "error", title: "Sorry, request failed to submit."}); // this error occurs when endpoint is wrong
                });
        },

        onFileChange(e) {
            var files = e.target.files || e.dataTransfer.files;
            if (!files.length) return;
            this.createPhoto(files[0]);
        },

        createPhoto(file) {
            var image = new Image();
            var reader = new FileReader();

            reader.onload = (e) => {
                this.step[0].photo = e.target.result;
            };
            reader.readAsDataURL(file);
        },
    },
};
</script>
